import { COPY_POLICIES, TITLE_COPY_LIMITS } from "./copy-policy";
import { validateScalarSlotLength } from "./scalar-slot-validation";
export const RESOURCE_CARDS_MAX_LENGTHS = {
    subheading: COPY_POLICIES.description.maxLength,
    resourceDescription: COPY_POLICIES.description.maxLength,
};
/**
 * The height a card draws every logo at, in CSS pixels.
 *
 * A RECOMMENDATION, not a floor. The card sets this height and lets the width
 * follow, so a picture shorter than twice this looks soft on a high-DPI screen —
 * but it is still the Editor's logo, and refusing it would refuse artwork the
 * page displays correctly. Nothing enforces this number; it is what the CMS
 * tells an Editor to aim for.
 */
export const RESOURCE_CARD_LOGO_HEIGHT = 41;
/**
 * The logo slot — UNFRAMED, so a logo is stored exactly as it was uploaded.
 *
 * Wordmarks are not one shape. The live band's own run from a square roundel to
 * a lockup six times as wide, and every one of them is drawn at a common height
 * with the width left to follow. A framed slot cannot express that: a target is
 * a fixed box and the renderer extracts a region to fill it, so whatever box
 * were chosen here would cut the top and bottom off the square marks or the
 * sides off the wide ones. The component constrains the height in CSS
 * (`h-[41px] w-auto object-contain`) and lets the artwork keep its proportions.
 *
 * So there is NO SIZE FLOOR on this slot, which is the Editor-visible point:
 * any logo the library holds can be placed, at whatever size and shape it
 * happens to be. {@link RESOURCE_CARD_LOGO_HEIGHT} is advice, not a gate.
 *
 * It also makes an SVG logo work, which a framed slot cannot: `createCrop`
 * leaves a vector original unrasterized, so a framed SVG names a rendition that
 * was never written. Unframed, the vector is delivered and stays crisp.
 *
 * `accepts` is deliberately unset, which reads as pictures only. A repeat item's
 * stored reference carries no media type — nothing writes one and nothing reads
 * one back — so a slot that accepted video would offer the Editor a clip and
 * then lose the fact that it was one. A wordmark is a picture.
 */
export const RESOURCE_CARD_LOGO_SLOT = {
    key: "logo",
    label: "Logo",
    unframed: true,
    targets: [],
};
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Does this part say anything at all? */
function saysNothing(value) {
    return typeof value !== "string" || value.trim() === "";
}
/** The inverse, for the optional parts where presence is what matters. */
function saysSomething(value) {
    return !saysNothing(value);
}
/** Validate a whole Resource Cards band. */
export function validateResourceCards(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The Resource Cards block is malformed" };
    }
    const { heading, sectionOneLabel, sectionTwoLabel, sectionOneResources, sectionTwoResources, } = value;
    if (saysNothing(heading)) {
        return { valid: false, reason: "A resource cards block needs a title" };
    }
    const titleLength = validateScalarSlotLength({ ...TITLE_COPY_LIMITS, label: "Title" }, heading);
    if (!titleLength.valid)
        return titleLength;
    if (typeof value.subheading === "string") {
        const subheadingLength = validateScalarSlotLength({
            label: "Description",
            maxLength: RESOURCE_CARDS_MAX_LENGTHS.subheading,
        }, value.subheading);
        if (!subheadingLength.valid)
            return subheadingLength;
    }
    if (!Array.isArray(sectionOneResources) ||
        !Array.isArray(sectionTwoResources)) {
        return { valid: false, reason: "The Resource Cards block is malformed" };
    }
    if (sectionOneResources.length === 0) {
        return {
            valid: false,
            reason: "The first section needs at least one resource",
        };
    }
    // A section is a label and the resources in it, or neither. Both halves of the
    // rule are one message because they are one rule reached from opposite sides.
    const secondIsLabelled = saysSomething(sectionTwoLabel);
    const secondIsFilled = sectionTwoResources.length > 0;
    if (secondIsLabelled !== secondIsFilled) {
        return {
            valid: false,
            reason: "A second section is a label and the resources in it, or neither",
        };
    }
    // Only once a second section exists does the first one's label get rendered —
    // it becomes half of the toggle, and a toggle with a nameless half cannot be
    // used. With one section it is never shown, so it is never required.
    if (secondIsFilled && saysNothing(sectionOneLabel)) {
        return {
            valid: false,
            reason: "The first section needs a label once there is a second",
        };
    }
    for (const resources of [sectionOneResources, sectionTwoResources]) {
        for (const resource of resources) {
            if (!isRecord(resource)) {
                return {
                    valid: false,
                    reason: "The Resource Cards block is malformed",
                };
            }
            if (saysNothing(resource.name)) {
                return { valid: false, reason: "Every resource needs a name" };
            }
            if (typeof resource.description === "string") {
                const descriptionLength = validateScalarSlotLength({
                    label: "Description",
                    maxLength: RESOURCE_CARDS_MAX_LENGTHS.resourceDescription,
                }, resource.description);
                if (!descriptionLength.valid)
                    return descriptionLength;
            }
            // The one part with no empty rendering: words the card would keep and
            // never draw, because it draws the link only when there is an address.
            if (saysSomething(resource.linkText) && saysNothing(resource.url)) {
                return {
                    valid: false,
                    reason: "A resource with link text needs a link address",
                };
            }
        }
    }
    return {
        valid: true,
        resourceCards: value,
    };
}
/** Minimum complete content for the tab-based editor and page publication. */
export function collectResourceCardsPublishIssues(block) {
    const scalars = block.scalars ?? {};
    const repeats = block.repeats ?? {};
    const tabs = (repeats.sections ?? []).filter((tab) => tab.scalars?.label?.trim());
    const hasLegacyTab = Boolean(scalars.sectionOneLabel?.trim() || scalars.sectionTwoLabel?.trim());
    if (!tabs.length && !hasLegacyTab) {
        const row = repeats.sections?.[0];
        return [
            {
                message: "Resource Cards needs at least one tab with a label.",
                fieldKind: row ? "scalar" : "group",
                fieldKey: row ? "label" : "sections",
                repeatKey: "sections",
                itemId: row?.id ?? row?.itemId,
            },
        ];
    }
    const namedResource = (rows = []) => rows.some((row) => Boolean(row.scalars?.name?.trim()));
    const hasResource = (Boolean(scalars.sectionOneLabel?.trim()) &&
        namedResource(repeats.sectionOneResources)) ||
        (Boolean(scalars.sectionTwoLabel?.trim()) &&
            namedResource(repeats.sectionTwoResources)) ||
        tabs.some((tab) => {
            const id = tab.id ?? tab.itemId;
            return (Boolean(id) &&
                namedResource((repeats.resources ?? []).filter((row) => row.scalars?.sectionId === id)));
        });
    if (hasResource)
        return [];
    const tab = tabs[0];
    const repeatKey = tab
        ? "resources"
        : scalars.sectionOneLabel?.trim()
            ? "sectionOneResources"
            : "sectionTwoResources";
    const row = (repeats[repeatKey] ?? []).find((row) => !tab || row.scalars?.sectionId === (tab.id ?? tab.itemId));
    return [
        {
            message: "Resource Cards needs at least one named resource inside a labelled tab.",
            fieldKind: row ? "scalar" : "group",
            fieldKey: row ? "name" : repeatKey,
            repeatKey,
            itemId: row?.id ?? row?.itemId,
        },
    ];
}
export function validateResourceCardsBlock(block) {
    const [issue] = collectResourceCardsPublishIssues(block);
    return issue ? { valid: false, reason: issue.message } : { valid: true };
}
/** Apply saved tab order, retaining newly added tabs and ignoring removed ids. */
export function orderResourceTabs(tabs, storedOrder) {
    let ids = [];
    try {
        const value = JSON.parse(storedOrder ?? "[]");
        if (Array.isArray(value))
            ids = value.filter((id) => typeof id === "string");
    }
    catch {
        /* Older blocks have no saved order. */
    }
    const rank = new Map();
    ids.forEach((id, index) => {
        if (!rank.has(id))
            rank.set(id, index);
    });
    return [...tabs].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
}
//# sourceMappingURL=resource-cards.js.map